import { AssetAnalyticsFact } from '../../services/dashboardService';

/**
 * Cross-filtering for the Analytics → Assets tab (User Story 108),
 * mirroring `licenseAnalyticsModel.ts` (User Story 107) and
 * `cloudAnalyticsModel.ts` (User Story 100).
 *
 * The server ships one fact row per asset (`asset-facts`, gated on
 * `analytics.assets.view`, no cost fields); this module re-derives the
 * cross-filterable charts — department, manufacturer, vendor, model,
 * lifecycle stage, warranty status, age bucket, assigned-vs-unassigned —
 * from those rows under a selection, so clicking a slice costs nothing but
 * a re-render.
 *
 * Unlike Cloud, there is no separate client-side date range here — the
 * Assets tab's own `AnalyticsAdvancedFilters` drawer already narrows the
 * fact fetch server-side, so a fact row is already inside whatever the page
 * filter selected.
 */

export type AssetDimension =
    | 'department'
    | 'manufacturer'
    | 'vendor'
    | 'model'
    | 'lifecycleStage'
    | 'warrantyStatus'
    | 'ageBucket'
    | 'assignedStatus';

/** Selected keys per dimension. Within a dimension OR, across dimensions AND. */
export type AssetSelection = Partial<Record<AssetDimension, string[]>>;

export const ASSET_DIMENSION_LABELS: Record<AssetDimension, string> = {
    department: 'Department',
    manufacturer: 'Manufacturer',
    vendor: 'Vendor',
    model: 'Model',
    lifecycleStage: 'Lifecycle Stage',
    warrantyStatus: 'Warranty Status',
    ageBucket: 'Age',
    assignedStatus: 'Assigned',
};

// Every dimension name happens to match its fact field name here, but the
// map is kept explicit anyway — indexing fact[dimension] directly is exactly
// what broke the Licences tab's 'software' dimension (fact field
// `softwareName`, not `software`). See [[dimension-name-vs-field-name-mismatch]].
const DIMENSION_FIELD: Record<AssetDimension, keyof AssetAnalyticsFact> = {
    department: 'department',
    manufacturer: 'manufacturer',
    vendor: 'vendor',
    model: 'model',
    lifecycleStage: 'lifecycleStage',
    warrantyStatus: 'warrantyStatus',
    ageBucket: 'ageBucket',
    assignedStatus: 'assignedStatus',
};

/** Backend display order for the three dimensions Postgres returns sorted by count. */
const LIFECYCLE_STAGE_ORDER = ['Procured', 'Deployed', 'Maintained', 'Retired'];
const WARRANTY_STATUS_ORDER = ['Expired', 'Expiring soon', 'Active', 'Not recorded'];
const AGE_BUCKET_ORDER = ['< 1 year', '1-2 years', '2-3 years', '3-5 years', '5+ years', 'Unknown'];

/** Cap on categories shown in the long-tail breakdowns — mirrors Story 104's TOP_N. */
const TOP_N = 15;

const FIXED_ORDER: Partial<Record<AssetDimension, string[]>> = {
    lifecycleStage: LIFECYCLE_STAGE_ORDER,
    warrantyStatus: WARRANTY_STATUS_ORDER,
    ageBucket: AGE_BUCKET_ORDER,
};

/** Long-tail dimensions capped at TOP_N in the static distributions endpoint too. */
const TOP_N_DIMENSIONS: AssetDimension[] = ['manufacturer', 'vendor', 'model'];

const keyOf = (fact: AssetAnalyticsFact, dimension: AssetDimension): string => (
    String(fact[DIMENSION_FIELD[dimension]])
);

/** Looks up the display label for a selected key — used to render filter chips. */
export function labelForKey(
    facts: AssetAnalyticsFact[],
    dimension: AssetDimension,
    key: string,
): string {
    const fact = facts.find((f) => keyOf(f, dimension) === key);
    return fact ? keyOf(fact, dimension) : key;
}

export const isSelected = (
    selection: AssetSelection,
    dimension: AssetDimension,
    key: string,
): boolean => (selection[dimension] ?? []).includes(key);

export const selectionCount = (selection: AssetSelection): number =>
    Object.values(selection).reduce((total, keys) => total + (keys?.length ?? 0), 0);

/**
 * Adds or removes one key. `additive` (ctrl/cmd/shift-click) keeps the rest
 * of the dimension's keys; a plain click replaces them, which is what
 * "select this one" means to everyone who has not read a manual.
 */
export function toggleSelection(
    selection: AssetSelection,
    dimension: AssetDimension,
    key: string,
    additive: boolean,
): AssetSelection {
    const current = selection[dimension] ?? [];
    const next = current.includes(key)
        ? current.filter((k) => k !== key)
        : additive ? [...current, key] : [key];

    const updated: AssetSelection = { ...selection };
    if (next.length === 0) delete updated[dimension];
    else updated[dimension] = next;
    return updated;
}

/**
 * Rows matching every selected dimension except `exclude`.
 *
 * Excluding the visual's own dimension is what keeps a chart usable after you
 * click it: filter the department bar by its own selection and it collapses
 * to a single slice with no way back to the others.
 */
export function applySelection(
    facts: AssetAnalyticsFact[],
    selection: AssetSelection,
    exclude?: AssetDimension,
): AssetAnalyticsFact[] {
    const dimensions = (Object.keys(selection) as AssetDimension[])
        .filter((d) => d !== exclude && (selection[d]?.length ?? 0) > 0);
    if (dimensions.length === 0) return facts;
    return facts.filter((fact) =>
        dimensions.every((d) => selection[d]!.includes(keyOf(fact, d))),
    );
}

export interface AssetAnalyticsBreakdownRow {
    key: string;
    label: string;
    count: number;
}

function breakdown(
    facts: AssetAnalyticsFact[],
    dimension: AssetDimension,
): AssetAnalyticsBreakdownRow[] {
    const rows = new Map<string, AssetAnalyticsBreakdownRow>();
    facts.forEach((fact) => {
        const key = keyOf(fact, dimension);
        const existing = rows.get(key);
        if (existing) existing.count += 1;
        else rows.set(key, { key, label: key, count: 1 });
    });

    const order = FIXED_ORDER[dimension];
    const sorted = order
        ? [...rows.values()].sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label))
        : [...rows.values()].sort((a, b) => b.count - a.count);

    return TOP_N_DIMENSIONS.includes(dimension) ? sorted.slice(0, TOP_N) : sorted;
}

export interface AssetAnalyticsView {
    byDepartment: AssetAnalyticsBreakdownRow[];
    byManufacturer: AssetAnalyticsBreakdownRow[];
    byVendor: AssetAnalyticsBreakdownRow[];
    byModel: AssetAnalyticsBreakdownRow[];
    byLifecycleStage: AssetAnalyticsBreakdownRow[];
    byWarrantyStatus: AssetAnalyticsBreakdownRow[];
    byAge: AssetAnalyticsBreakdownRow[];
    assignedVsUnassigned: AssetAnalyticsBreakdownRow[];
    scopedCount: number;
}

export function aggregate(
    facts: AssetAnalyticsFact[],
    selection: AssetSelection,
): AssetAnalyticsView {
    const scoped = applySelection(facts, selection);

    return {
        // Each breakdown ignores its own dimension's selection — see applySelection.
        byDepartment: breakdown(applySelection(facts, selection, 'department'), 'department'),
        byManufacturer: breakdown(applySelection(facts, selection, 'manufacturer'), 'manufacturer'),
        byVendor: breakdown(applySelection(facts, selection, 'vendor'), 'vendor'),
        byModel: breakdown(applySelection(facts, selection, 'model'), 'model'),
        byLifecycleStage: breakdown(applySelection(facts, selection, 'lifecycleStage'), 'lifecycleStage'),
        byWarrantyStatus: breakdown(applySelection(facts, selection, 'warrantyStatus'), 'warrantyStatus'),
        byAge: breakdown(applySelection(facts, selection, 'ageBucket'), 'ageBucket'),
        assignedVsUnassigned: breakdown(applySelection(facts, selection, 'assignedStatus'), 'assignedStatus'),
        scopedCount: scoped.length,
    };
}
